import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    FaDollarSign,
    FaShoppingCart,
    FaTruck,
    FaHome,
    FaFileAlt,
    FaClock,
    FaTasks,
    FaEye,
    FaEdit,
    FaPrint,
    FaAngleLeft,
    FaAngleRight,
    FaInfoCircle,
    FaSearch,
    FaTrash,
    FaPlus,
    FaJediOrder,
    FaDiscord,
} from 'react-icons/fa';
import { Axe } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import bag from '../../assets/icons/bag.png';
import { FaShoelace, FaShopSlash } from 'react-icons/fa6';
import { ClipLoader } from 'react-spinners';

export default function OrderList() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 480);
    const [confirmDelete, setConfirmDelete] = useState({ open: false, orderId: null });
    const [deleting, setDeleting] = useState(false);
    const [filterLoading, setFilterLoading] = useState(false);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        totalPages: 1,
        totalOrders: 0
    });
    const [selectedStatus, setSelectedStatus] = useState('All Status');
    const [orders, setOrders] = useState([]);
    const [allOrders, setAllOrders] = useState([]); // Store paginated unfiltered orders
    const [fullOrders, setFullOrders] = useState([]); // Store all orders for search
    const [analytics, setAnalytics] = useState({
        orders: {
            total: 0,
            pending: 0,
            inProgress: 0,
            confirmed: 0,
            outForDelivery: 0,
            orderDelivery: 0,
            cancelled: 0,
            returned: 0
        },
        reviews: {
            total: 0,
            pending: 0
        }
    });
    const [totalOrder, setTotalOrder] = useState(0);

    useEffect(() => {
        const handleResize = () => {
            const currentWidth = window.innerWidth;
            setIsMobile(currentWidth <= 768);
            setIsSmallMobile(currentWidth <= 480);
        };

        window.addEventListener('resize', handleResize);
        handleResize();
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Debounce search term
    useEffect(() => {
        if (searchTerm === '') {
            setDebouncedSearchTerm('');
        } else {
            const timer = setTimeout(() => {
                setDebouncedSearchTerm(searchTerm);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [searchTerm]);

    // Apply filters and pagination
    useEffect(() => {
        let sourceOrders = debouncedSearchTerm ? fullOrders : allOrders;
        let filteredOrders = sourceOrders.filter(order =>
            !debouncedSearchTerm ||
            (order.orderNumber && order.orderNumber.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())) ||
            (order.courierService && order.courierService.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase()))
        );

        if (debouncedSearchTerm) {
            const startIdx = (pagination.page - 1) * pagination.limit;
            const endIdx = startIdx + pagination.limit;
            const paginatedOrders = filteredOrders.slice(startIdx, endIdx);
            setOrders(paginatedOrders);
            setPagination(prev => ({
                ...prev,
                totalPages: Math.ceil(filteredOrders.length / prev.limit),
                totalOrders: filteredOrders.length
            }));
        } else {
            setOrders(allOrders);
        }
    }, [debouncedSearchTerm, fullOrders, allOrders, pagination.page, pagination.limit]);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            let url = `https://api.alburaqscents.com/v1/order?page=${pagination.page}&limit=${pagination.limit}`;

            if (selectedStatus !== 'All Status') {
                url += `&status=${selectedStatus}`;
            }

            const response = await axios.get(url);
            const result = response.data;

            if (result.success) {
                const processedOrders = result.data.map(order => ({
                    ...order,
                    id: order._id.substring(18),
                    customer: order.customerId?.name || 'Unknown',
                    qty: order.items.reduce((sum, item) => sum + item.quantity, 0),
                    total: order.totalAmount,
                    paymentStatus: order.paymentMethod === 'COD' ? 'Pending' : 'Paid',
                    createdAt: new Date(order.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                    }),
                    status: order.orderStatus.toLowerCase()
                }));

                setAllOrders(processedOrders);
                setPagination({
                    page: result.currentPage,
                    limit: pagination.limit,
                    totalPages: result.totalPages,
                    totalOrders: result.totalOrders
                });
            } else {
                toast.error(result.message || 'Failed to load orders');
            }
        } catch (error) {
            console.error('Error fetching orders:', error);
            toast.error('Network error while loading orders');
        } finally {
            setLoading(false);
            setFilterLoading(false);
        }
    };

    const fetchAllOrders = async () => {
        try {
            let url = `https://api.alburaqscents.com/v1/order?page=1&limit=10000`;

            if (selectedStatus !== 'All Status') {
                url += `&status=${selectedStatus}`;
            }

            const response = await axios.get(url);
            const result = response.data;

            if (result.success) {
                return result.data.map(order => ({
                    ...order,
                    id: order._id.substring(18),
                    customer: order.customerId?.name || 'Unknown',
                    qty: order.items.reduce((sum, item) => sum + item.quantity, 0),
                    total: order.totalAmount,
                    paymentStatus: order.paymentMethod === 'COD' ? 'Pending' : 'Paid',
                    createdAt: new Date(order.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                    }),
                    status: order.orderStatus.toLowerCase()
                }));
            } else {
                toast.error(result.message || 'Failed to load all orders');
                return [];
            }
        } catch (error) {
            console.error('Error fetching all orders:', error);
            toast.error('Network error while loading all orders');
            return [];
        }
    };

    // Fetch paginated orders when not searching
    useEffect(() => {
        if (!debouncedSearchTerm) {
            fetchOrders();
        }
    }, [pagination.page, selectedStatus, debouncedSearchTerm]);

    // Fetch all orders for search when needed
    useEffect(() => {
        if (debouncedSearchTerm && fullOrders.length === 0) {
            const fetchFull = async () => {
                setFilterLoading(true);
                const processedOrders = await fetchAllOrders();
                setFullOrders(processedOrders);
                setFilterLoading(false);
            };
            fetchFull();
        }
    }, [debouncedSearchTerm, fullOrders.length, selectedStatus]);

    // Clear fullOrders when status changes during search
    const handleStatusChange = (e) => {
        const newStatus = e.target.value;
        setSelectedStatus(newStatus);
        setPagination(prev => ({ ...prev, page: 1 }));
        setFilterLoading(true);
        if (debouncedSearchTerm) {
            setFullOrders([]);
        }
    };

    const handleStatusCardClick = (status) => {
        if (selectedStatus !== status) {
            setSelectedStatus(status);
            setPagination(prev => ({ ...prev, page: 1 }));
            setFilterLoading(true);
            if (debouncedSearchTerm) {
                setFullOrders([]);
            }
        }
    };

    const fetchAnalytics = async () => {
        try {
            const response = await axios.get('https://api.alburaqscents.com/v1/order/analytic');
            if (response.data.success) {
                setAnalytics(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching analytics:', error);
            toast.error('Failed to load analytics');
        }
    };

    useEffect(() => {
        fetchAnalytics();
    }, []);

    const handleViewOrder = (orderId) => {
        navigate(`/orders/details/${orderId}`);
    };

    const handleEditOrder = (orderId) => {
        navigate(`/edit-order/${orderId}`);
    };

    const handleDeleteOrder = (orderId) => {
        setConfirmDelete({ open: true, orderId });
    };

    const confirmDeletion = async () => {
        const orderId = confirmDelete.orderId;
        setDeleting(true);
        try {
            const response = await axios.delete(`https://api.alburaqscents.com/v1/order/${orderId}`);

            if (response.data.success) {
                toast.success('Order deleted successfully');
                if (debouncedSearchTerm) {
                    setFullOrders([]);
                }
                fetchOrders();
                fetchAnalytics();
            } else {
                toast.error(response.data.message || 'Failed to delete order');
            }
        } catch (error) {
            toast.error('Network error while deleting order');
        } finally {
            setDeleting(false);
            setConfirmDelete({ open: false, orderId: null });
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination(prev => ({ ...prev, page: newPage }));
        }
    };

    const handleSearchChange = (e) => {
        const newTerm = e.target.value;
        setSearchTerm(newTerm);
        setPagination(prev => ({ ...prev, page: 1 }));
        if (!newTerm) {
            setFullOrders([]);
        }
    };

    const formatStatus = (status) => {
        switch (status) {
            case 'pending':
                return 'Pending';
            case 'confirmed':
                return 'Confirmed';
            case 'inProgress':
                return 'In Progress';
            case 'outForDelivery':
                return 'Out for Delivery';
            case 'orderDelivery':
                return 'Delivered';
            case 'delivered':
                return 'Delivered';
            case 'cancelled':
                return 'Cancelled';
            case 'returned':
                return 'Returned';
            default:
                return status;
        }
    };

    const generatePrintHTML = (orderData) => {
        const currentDate = new Date().toLocaleDateString("en-US", {
            day: "numeric",
            month: "long",
            year: "numeric",
        });

        return `
        <!DOCTYPE html>
        <html>
        <head>
            <style>
                * { margin: 0; padding: 0; box-sizing: border-box; }
                body { 
                    font-family: Arial, sans-serif; 
                    font-size: 10px; 
                    line-height: 1.2; 
                    color: #000; 
                    background: white; 
                    margin: 0;
                    padding: 0;
                }
                .print-container { 
                    width: 100%; 
                    max-width: 1000px; 
                    margin: 0 auto; 
                    padding: 10px;
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }
                .header-section { 
                    display: flex; 
                    justify-content: space-between; 
                    align-items: center;
                    border-bottom: 2px solid #000;
                    padding-bottom: 8px;
                    margin-bottom: 5px;
                }
                .company-info { 
                    text-align: left;
                }
                .company-name { 
                    font-size: 16px; 
                    font-weight: bold; 
                }
                .order-info { 
                    text-align: right;
                    font-size: 9px;
                }
                .main-content { 
                    display: flex; 
                    gap: 15px;
                    min-height: 300px;
                }
                .left-column { 
                    flex: 1; 
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }
                .right-column { 
                    flex: 1; 
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }
                .info-box { 
                    border: 1px solid #000; 
                    padding: 6px;
                    border-radius: 3px;
                }
                .box-title { 
                    font-size: 9px; 
                    font-weight: bold; 
                    margin-bottom: 3px;
                    border-bottom: 1px solid #ccc;
                    padding-bottom: 2px;
                }
                .items-table { 
                    width: 100%; 
                    border-collapse: collapse; 
                    font-size: 8px;
                    margin-top: 5px;
                }
                .items-table th, .items-table td { 
                    border: 1px solid #000; 
                    padding: 3px; 
                    text-align: left;
                }
                .items-table th { 
                    background: #f0f0f0; 
                    font-weight: bold;
                    font-size: 8px;
                }
                .courier-instructions { 
                    border: 1px solid #000;
                    padding: 6px;
                    margin-top: 5px;
                    font-size: 9px;
                }
                .payment-highlight {
                    font-weight: bold;
                    color: #d00;
                    background: #fff0f0;
                    padding: 2px 4px;
                    border-radius: 2px;
                }
                .barcode-area {
                    text-align: center;
                    margin-top: 5px;
                    padding: 5px;
                    border: 1px dashed #ccc;
                }
                .signature-area { 
                    display: flex; 
                    justify-content: space-between; 
                    margin-top: 10px;
                    font-size: 8px;
                }
                .signature-box { 
                    width: 120px; 
                    text-align: center;
                }
                .signature-line { 
                    border-bottom: 1px solid #000; 
                    height: 25px; 
                    margin-bottom: 2px;
                }
                .urgent-stamp {
                    position: absolute;
                    top: 50px;
                    right: 50px;
                    border: 2px solid red;
                    padding: 5px 10px;
                    transform: rotate(15deg);
                    color: red;
                    font-weight: bold;
                    background: white;
                }
                
                /* Print-specific styles */
                @page {
                    size: landscape;
                    margin: 0.5cm;
                }
                @media print {
                    body { 
                        margin: 0; 
                        padding: 0; 
                        -webkit-print-color-adjust: exact; 
                        print-color-adjust: exact;
                        width: 100%;
                    }
                    .print-container { 
                        margin: 0; 
                        padding: 10px; 
                        width: 100%;
                        max-width: none;
                    }
                    .no-print { display: none; }
                }
            </style>
        </head>
        <body>
            <div class="print-container">
                <!-- Header Section -->
                <div class="header-section">
                    <div class="company-info">
                        <div class="company-name">ALBURAQ SCENTS</div>
                        <div style="font-size: 9px;">Premium Fragrance Collection</div>
                    </div>
                    <div class="order-info">
                        <strong>ORDER #: ${orderData.id || orderData.orderNumber || 'N/A'}</strong><br>
                        Date: ${orderData.createdAt ? new Date(orderData.createdAt).toLocaleDateString() : 'N/A'}<br>
                        Print: ${currentDate}
                    </div>
                </div>

                <!-- Main Content - Two Columns -->
                <div class="main-content">
                    <!-- Left Column -->
                    <div class="left-column">
                        <!-- Shipping Information -->
                        <div class="info-box">
                            <div class="box-title">SHIP TO</div>
                            <strong>${orderData.customer || orderData.customerId?.name || "N/A"}</strong><br>
                            📞 ${orderData.shippingAddress?.phoneNumber || "N/A"}<br>
                            📧 ${orderData.customerId?.email || "N/A"}<br><br>
                            <strong>Address:</strong><br>
                            ${orderData.shippingAddress?.address || "N/A"}<br>
                            ${orderData.shippingAddress?.city || ""}, ${orderData.shippingAddress?.state || ""}<br>
                            ${orderData.shippingAddress?.country || ""}<br>
                            ${orderData.shippingAddress?.famousSpot ? `<strong>Landmark:</strong> ${orderData.shippingAddress.famousSpot}` : ""}
                        </div>

                        <!-- Order Items -->
                        <div class="info-box">
                            <div class="box-title">ORDER ITEMS (${orderData.items?.length || 0})</div>
                            <table class="items-table">
                                <thead>
                                    <tr>
                                        <th>Product</th>
                                        <th>SKU</th>
                                        <th>Size</th>
                                        <th>Qty</th>
                                        <th>Price</th>
                                        <th>Total</th>
                                    </tr>
                                </thead>
                                <tbody>+
                                    ${orderData.items?.map((item) => {
            // console.log("------------>>>>", item);

            const selectedSize = item.productId?.sizes?.find(
                size => size._id === item.selectedSizeId
            );
            return `
                                            <tr>
                                                <td>${item.productId?.name || 'N/A'}</td>
                                                <td>${selectedSize?.sku || 'N/A'}</td>
                                                <td>${selectedSize?.ml ? selectedSize.ml + 'ML' : 'N/A'}</td>
                                                <td>${item.quantity}</td>
                                                <td>PKR ${item.priceAtPurchase?.toFixed(2) || '0.00'}</td>
                                                <td>PKR ${(item.quantity * item.priceAtPurchase)?.toFixed(2) || '0.00'}</td>
                                            </tr>
                                        `;
        }).join('') || '<tr><td colspan="6">No items found</td></tr>'}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <!-- Right Column -->
                    <div class="right-column">
                        <!-- Payment & Order Info -->
                        <div class="info-box">
                            <div class="box-title">ORDER SUMMARY</div>
                            <div class="payment-highlight">
                                ${orderData.paymentMethod === 'COD'
                ? "💰 CASH ON DELIVERY - COLLECT: PKR " + (orderData.total?.toFixed(2) || '0.00')
                : "✅ PREPAID - NO COLLECTION REQUIRED"}
                            </div>
                            <br>
                            <strong>Status:</strong> ${formatStatus(orderData.status) || orderData.orderStatus?.toUpperCase() || 'N/A'}<br>
                            <strong>Total Amount:</strong> PKR ${orderData.total?.toFixed(2) || '0.00'}<br>
                            <strong>Items:</strong> ${orderData.items?.length || 0} product(s)<br>
                            <strong>Total Qty:</strong> ${orderData.items?.reduce((sum, item) => sum + item.quantity, 0) || 0}<br>
                            <strong>Subtotal:</strong> PKR ${orderData.items?.reduce((total, item) => total + item.quantity * item.priceAtPurchase, 0)?.toFixed(2) || '0.00'}
                        </div>

                        <!-- Courier Instructions -->
                        <div class="courier-instructions">
                            <div class="box-title">COURIER INSTRUCTIONS</div>
                            • 📞 <strong>CONTACT BEFORE DELIVERY: ${orderData.shippingAddress?.phoneNumber || 'N/A'}</strong><br>
                            • 🎁 Handle with care - Fragile items<br>
                            • 📍 Verify address with recipient<br>
                            • ⏰ Deliver during business hours<br>
                            • 📝 Get signature upon delivery<br>
                            • 🏠 <strong>Address:</strong> ${orderData.shippingAddress?.address || 'N/A'}, ${orderData.shippingAddress?.city || 'N/A'}
                        </div>

                        <!-- Barcode Area -->
                        <div class="barcode-area">
                            <div style="font-size: 8px; margin-bottom: 3px;">TRACKING #: ${orderData.id || orderData.orderNumber || 'N/A'}</div>
                            <div style="font-family: 'Courier New', monospace; font-size: 18px; letter-spacing: 1px; font-weight: bold;">
                                | ${orderData.id || orderData.orderNumber || 'N/A'} |
                            </div>
                            <div style="font-size: 7px; margin-top: 2px;">Scan above barcode for tracking</div>
                        </div>
                    </div>
                </div>

                <!-- Footer Signatures -->
                <div class="signature-area">
                    <div class="signature-box">
                        <div class="signature-line"></div>
                        <div>Customer Signature & Date</div>
                    </div>
                    <div class="signature-box">
                        <div class="signature-line"></div>
                        <div>Courier Agent Signature</div>
                    </div>
                    <div class="signature-box">
                        <div class="signature-line"></div>
                        <div>Received in Good Condition</div>
                    </div>
                </div>

               
            </div>
        </body>
        </html>
    `;
    };


    const handlePrintOrders = async () => {
        const allOrdersToPrint = await fetchAllOrders();
        if (allOrdersToPrint.length === 0) {
            toast.error('No orders available to print');
            return;
        }

        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
        <html>
            <head>
                <title>Order Invoices</title>
                <style>
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    body { font-family: Arial, sans-serif; font-size: 12px; line-height: 1.4; color: #000; background: white; }
                    .print-container { max-width: 800px; margin: 0 auto; padding: 20px; min-height: 100%; }
                    .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 15px; margin-bottom: 20px; }
                    .company-name { font-size: 24px; font-weight: bold; margin-bottom: 5px; }
                    .document-title { font-size: 18px; font-weight: bold; margin-top: 10px; }
                    .order-info { display: flex; justify-content: space-between; margin-bottom: 20px; padding: 10px; background: #f5f5f5; border: 1px solid #ddd; }
                    .info-section { margin-bottom: 20px; }
                    .section-title { font-size: 14px; font-weight: bold; margin-bottom: 8px; padding: 5px; background: #e0e0e0; border-left: 4px solid #333; }
                    .two-column { display: flex; gap: 20px; }
                    .column { flex: 1; }
                    .address-box { border: 2px solid #000; padding: 15px; margin-bottom: 15px; }
                    .items-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
                    .items-table th, .items-table td { border: 1px solid #000; padding: 8px; text-align: left; }
                    .items-table th { background: #f0f0f0; font-weight: bold; }
                    .total-section { float: right; width: 300px; margin-top: 20px; }
                    .total-row { display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px solid #ddd; }
                    .total-final { font-weight: bold; font-size: 16px; border-top: 2px solid #000; padding-top: 10px; }
                    .courier-section { margin-top: 30px; border: 2px solid #000; padding: 15px; }
                    .signature-area { margin-top: 30px; display: flex; justify-content: space-between; }
                    .signature-box { width: 200px; text-align: center; }
                    .signature-line { border-bottom: 1px solid #000; height: 40px; margin-bottom: 5px; }
                    @media print {
                        @page { margin: 0; size: auto; }
                        body { margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                        .print-container { margin: 0; padding: 15px; width: 100%; page-break-inside: avoid; page-break-after: always; }
                        html, body { height: auto; overflow: visible; }
                    }
                </style>
            </head>
            <body>
                ${allOrdersToPrint.map(order => generatePrintHTML(order)).join('')}
            </body>
        </html>
    `);
        printWindow.document.close();

        printWindow.onload = () => {
            setTimeout(() => {
                printWindow.print();
                printWindow.close();
            }, 1000);
        };
    };

    const renderPaginationButtons = () => {
        const buttons = [];
        const maxVisibleButtons = 5;
        let startPage = 1;
        let endPage = pagination.totalPages;

        if (pagination.totalPages > maxVisibleButtons) {
            const half = Math.floor(maxVisibleButtons / 2);
            if (pagination.page <= half) {
                endPage = maxVisibleButtons;
            } else if (pagination.page + half >= pagination.totalPages) {
                startPage = pagination.totalPages - maxVisibleButtons + 1;
            } else {
                startPage = pagination.page - half;
                endPage = pagination.page + half;
            }
        }

        if (startPage > 1) {
            buttons.push(
                <button
                    key={1}
                    style={styles.paginationButton}
                    onClick={() => handlePageChange(1)}
                >
                    1
                </button>
            );
            if (startPage > 2) {
                buttons.push(<span key="start-ellipsis" style={styles.paginationText}>...</span>);
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            buttons.push(
                <button
                    key={i}
                    style={{
                        ...styles.paginationButton,
                        ...(i === pagination.page && styles.paginationButtonActive)
                    }}
                    onClick={() => handlePageChange(i)}
                >
                    {i}
                </button>
            );
        }

        if (endPage < pagination.totalPages) {
            if (endPage < pagination.totalPages - 1) {
                buttons.push(<span key="end-ellipsis" style={styles.paginationText}>...</span>);
            }
            buttons.push(
                <button
                    key={pagination.totalPages}
                    style={styles.paginationButton}
                    onClick={() => handlePageChange(pagination.totalPages)}
                >
                    {pagination.totalPages}
                </button>
            );
        }

        return buttons;
    };


    const styles = {
        mainWrapper: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            minHeight: '100vh',
            width: '100%',
            backgroundColor: 'transparent',
            padding: isSmallMobile ? '0px' : (isMobile ? '15px' : '10px'),
            boxSizing: 'border-box',
            fontFamily: 'Inter, sans-serif',
            overflowX: 'hidden',
        },
        cardValueContainer: {
            backgroundColor: '#E5D0A5',
            width: '40px',
            height: '40px',
            borderRadius: '3px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
        },
        orderListPageContainer: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            maxWidth: '1200px',
            backgroundColor: 'transparent',
            padding: isSmallMobile ? '15px' : '20px',
            boxSizing: 'border-box',
        },
        headerTitle: {
            fontSize: isSmallMobile ? '20px' : (isMobile ? '24px' : '30px'),
            color: '#000',
            marginBottom: isSmallMobile ? '15px' : '20px',
            fontWeight: 'bold',
            textAlign: 'left',
        },
        cardsContainer: {
            display: 'grid',
            gridTemplateColumns: isSmallMobile ? '1fr' : (isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)'),
            gap: isSmallMobile ? '10px' : '15px',
            marginBottom: isSmallMobile ? '15px' : '20px',
        },
        card: {
            backgroundColor: '#000',
            borderRadius: '8px',
            padding: isSmallMobile ? '8px' : '12px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            color: '#E5D0A5',
            minHeight: isSmallMobile ? '70px' : '100px',
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
            cursor: 'pointer',
        },
        cardHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '3px',
        },
        cardTitle: {
            fontSize: isSmallMobile ? '12px' : '14px',
            margin: 0,
        },
        cardIcon: {
            fontSize: isSmallMobile ? '16px' : '20px',
            color: 'black',
        },
        cardValue: {
            fontSize: isSmallMobile ? '18px' : '24px',
            textAlign: 'left',
            paddingTop: '12px'
        },
        allOrderListContainer: {
            backgroundColor: '#000',
            borderRadius: '10px',
            padding: isSmallMobile ? '15px' : '20px',
            color: '#E5D0A5',
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
        },
        allOrderListHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '15px',
            flexDirection: isSmallMobile ? 'column' : 'row',
            gap: isSmallMobile ? '10px' : '0',
            textAlign: isSmallMobile ? 'center' : 'left',
        },
        allOrderListTitle: {
            fontSize: isSmallMobile ? '16px' : '28px',
            margin: 0,
            fontWeight: '500',
            color: '#F9D978',
        },
        searchFilterContainer: {
            display: 'flex',
            gap: '10px',
            flexWrap: 'wrap',
            justifyContent: isSmallMobile ? 'center' : 'flex-end',
            width: isSmallMobile ? '100%' : 'auto',
        },
        searchInputWrapper: {
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
        },
        searchInput: {
            backgroundColor: '#333',
            color: '#E5D0A5',
            border: '1px solid #555',
            borderRadius: '5px',
            padding: '8px 10px 8px 30px',
            fontSize: '12px',
            outline: 'none',
            width: isSmallMobile ? '150px' : '180px',
        },
        searchIcon: {
            position: 'absolute',
            left: '10px',
            color: '#AAA',
            fontSize: '14px',
        },
        selectFilter: {
            backgroundColor: '#333',
            color: '#E5D0A5',
            border: '1px solid #555',
            borderRadius: '5px',
            padding: '8px 10px',
            fontSize: '12px',
            outline: 'none',
            appearance: 'none',
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3E%3Cpath fill='%23E5D0A5' d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 8px center',
            backgroundSize: '12px',
            cursor: 'pointer',
        },
        tableWrapper: {
            overflowX: 'auto',
            marginBottom: '15px',
            flexGrow: 1,
            '::-webkit-scrollbar': {
                width: '8px',
                height: '8px',
            },
            '::-webkit-scrollbar-thumb': {
                backgroundColor: '#555',
                borderRadius: '4px',
            },
            '::-webkit-scrollbar-track': {
                backgroundColor: '#333',
            },
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
            minWidth: isMobile ? '700px' : '900px',
        },
        tableHeader: {
            backgroundColor: '#000',
            color: '#E5D0A5',
            fontSize: '12px',
            textAlign: 'left',
            padding: '8px 0',
            borderBottom: '1px solid #333',
        },
        th: {
            padding: '16px 10px',
            whiteSpace: 'nowrap',
        },
        tableRow: {
            borderBottom: '1px solid #333',
        },
        tableRowHover: {
            backgroundColor: '#1a1a1a',
        },
        td: {
            padding: '16px 10px',
            verticalAlign: 'middle',
            whiteSpace: 'nowrap',
            fontSize: '12px',
        },
        checkbox: {
            marginRight: '8px',
            backgroundColor: '#E5D0A5',
            border: '1px solid #E5D0A5',
            appearance: 'none',
            width: '14px',
            height: '14px',
            borderRadius: '3px',
            cursor: 'pointer',
            position: 'relative',
            outline: 'none',
            '&:checked': {
                backgroundColor: '#E5D0A5',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '2px',
                    left: '2px',
                    width: '8px',
                    height: '8px',
                    backgroundColor: '#000',
                    borderRadius: '1px',
                },
            },
        },
        statusPill: {
            display: 'inline-block',
            padding: '4px 8px',
            borderRadius: '5px',
            fontSize: '10px',
            fontWeight: 'bold',
            textAlign: 'center',
            color: '#000',
        },
        statusPaid: {
            backgroundColor: '#8BC34A',
            color: '#000',
        },
        statusPending: {
            backgroundColor: '#FFEB3B',
            color: '#000',
        },
        statusReady: {
            backgroundColor: '#8BC34A',
            color: '#000',
        },
        statusShipped: {
            backgroundColor: '#2196F3',
            color: '#FFF',
        },
        statusorderDelivery: {
            backgroundColor: '#4CAF50',
            color: '#FFF',
        },
        statusReturned: {
            backgroundColor: '#A855F7',
            color: '#FFF',
        },
        statusInactive: {
            backgroundColor: '#F44336',
            color: '#FFF',
        },
        actionButtons: {
            display: 'flex',
            gap: '4px',
            justifyContent: 'center',
        },
        actionButton: {
            backgroundColor: '#E5D0A5',
            border: 'none',
            borderRadius: '5px',
            padding: '5px 7px',
            cursor: 'pointer',
            fontSize: '12px',
            color: '#000',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.2s ease',
        },
        actionButtonHover: {
            backgroundColor: '#d0b98e',
        },
        actionButtonIcon: {
            fontSize: '13px',
        },
        paginationContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '15px',
            flexDirection: isSmallMobile ? 'column' : 'row',
            gap: isSmallMobile ? '10px' : '0',
            padding: '0 5px',
        },
        paginationControls: {
            display: 'flex',
            gap: '4px',
            flexWrap: 'wrap',
            justifyContent: isSmallMobile ? 'center' : 'flex-start',
        },
        paginationButton: {
            backgroundColor: '#E5D0A5',
            color: '#000',
            border: '1px solid #000',
            borderRadius: '5px',
            padding: '4px 8px',
            cursor: 'pointer',
            fontSize: '12px',
            transition: 'background-color 0.2s ease',
            minWidth: '25px',
            textAlign: 'center',
        },
        paginationButtonActive: {
            backgroundColor: '#000',
            color: '#E5D0A5',
        },
        paginationButtonHover: {
            backgroundColor: '#d0b98e',
        },
        productListContainer: {
            backgroundColor: '#000',
            borderRadius: '10px',
            padding: isSmallMobile ? '15px' : '20px',
            color: '#E5D0A5',
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column'
        },
        productListHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '15px',
            flexDirection: isSmallMobile ? 'column' : 'row',
            gap: isSmallMobile ? '10px' : '0',
            textAlign: isSmallMobile ? 'center' : 'left'
        },
        searchBar: {
            flexGrow: 1,
            minWidth: '200px',
            maxWidth: '400px',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            backgroundColor: 'transparent',
            borderRadius: '24px',
            border: '1px solid #D4AF37',
            padding: '10px 20px',
            height: '50px'
        },
        searchIcon: {
            color: '#D4AF37',
            fontSize: '17px',
            marginRight: '10px'
        },
        searchInput: {
            backgroundColor: 'transparent',
            border: 'none',
            outline: 'none',
            color: '#D4AF37',
            fontSize: '17px',
            flexGrow: 1,
            width: '100%'
        },
        searchFilterContainer: {
            display: 'flex',
            gap: '10px',
            flexWrap: 'wrap',
            justifyContent: isSmallMobile ? 'center' : 'flex-end',
            width: isSmallMobile ? '100%' : 'auto'
        },
        selectFilter: {
            width: '120px',
            backgroundColor: '#333',
            color: '#E5D0A5',
            border: '1px solid #555',
            borderRadius: '5px',
            padding: '8px 10px',
            fontSize: '12px',
            outline: 'none',
            appearance: 'none',
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3E%3Cpath fill='%23E5D0A5' d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 8px center',
            backgroundSize: '12px',
            cursor: 'pointer'
        },
        addButton: {
            backgroundColor: '#D4AF37',
            color: '#000',
            border: 'none',
            borderRadius: '5px',
            padding: '8px 15px',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: 'bold',
            transition: 'background-color 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
        },
        tableWrapper: {
            overflowX: 'auto',
            marginBottom: '15px',
            flexGrow: 1,
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
            minWidth: isMobile ? '700px' : '900px'
        },
        tableHeader: {
            backgroundColor: '#000',
            color: '#E5D0A5',
            fontSize: '12px',
            textAlign: 'left',
            padding: '8px 0',
            borderBottom: '1px solid #333'
        },
        th: {
            padding: '16px 10px',
            whiteSpace: 'nowrap'
        },
        tableRow: {
            borderBottom: '1px solid #333',
        },
        td: {
            padding: '16px 10px',
            verticalAlign: 'middle',
            whiteSpace: 'nowrap',
            fontSize: '12px'
        },
        statusPill: {
            display: 'inline-block',
            padding: '4px 8px',
            borderRadius: '5px',
            fontSize: '10px',
            fontWeight: 'bold',
            textAlign: 'center'
        },
        statusActive: {
            backgroundColor: '#8BC34A',
            color: '#000'
        },
        statusInactive: {
            backgroundColor: '#F44336',
            color: '#FFF'
        },
        outOfStock: {
            backgroundColor: '#FF9800',
            color: '#000'
        },
        actionButtons: {
            display: 'flex',
            gap: '4px',
            justifyContent: 'center'
        },
        actionButton: {
            backgroundColor: '#E5D0A5',
            border: 'none',
            borderRadius: '5px',
            padding: '5px 7px',
            cursor: 'pointer',
            fontSize: '12px',
            color: '#000',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.3s ease',
        },
        actionButtonDanger: {
            backgroundColor: '#F44336',
            color: '#FFF',
        },
        actionButtonIcon: {
            fontSize: '13px'
        },
        paginationContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '15px',
            flexDirection: isSmallMobile ? 'column' : 'row',
            gap: isSmallMobile ? '10px' : '0',
            padding: '0 5px'
        },
        paginationControls: {
            display: 'flex',
            gap: '4px',
            flexWrap: 'wrap',
            justifyContent: isSmallMobile ? 'center' : 'flex-start'
        },
        paginationButton: {
            backgroundColor: '#E5D0A5',
            color: '#000',
            border: '1px solid #000',
            borderRadius: '5px',
            padding: '4px 8px',
            cursor: 'pointer',
            fontSize: '12px',
            transition: 'background-color 0.3s ease',
            minWidth: '25px',
            textAlign: 'center',
        },
        paginationButtonActive: {
            backgroundColor: '#000',
            color: '#E5D0A5',
        },
        bagIconContainer: {
            position: 'relative',
            width: '120px',
            height: '120px',
            margin: '0 auto 0px',
        },
        mainBagIcon: {
            width: '100%',
            height: '100%',
            objectFit: 'contain',
        },
        smallBagIcon: {
            position: 'absolute',
            top: '-10px',
            right: '-10px',
            width: '30px',
            height: '30px',
            objectFit: 'contain',
            opacity: 0.7,
        },
        paginationText: {
            color: '#E5D0A5',
            fontSize: '12px',
            marginTop: isSmallMobile ? '8px' : '0'
        },
        '@keyframes spin': {
            '0%': { transform: 'rotate(0deg)' },
            '100%': { transform: 'rotate(360deg)' },
        },
        '@keyframes fadeIn': {
            '0%': { opacity: 0 },
            '100%': { opacity: 1 },
        },
        loadingContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
            width: '100%'
        },
        loadingSpinner: {
            border: '4px solid rgba(0, 0, 0, 0.1)',
            borderTop: '4px solid #E5D0A5',
            borderRadius: '50%',
            width: '30px',
            height: '30px',
            animation: 'spin 1s linear infinite'
        },
        emptyState: {
            textAlign: 'center',
            color: '#fff',
            animation: 'fadeIn 0.5s ease-in-out'
        }
    };

    const isSearchLoading = !!debouncedSearchTerm && fullOrders.length === 0;

    return (
        <div style={styles.mainWrapper}>
            <div style={styles.orderListPageContainer}>
                <h1 style={styles.headerTitle}>Orders</h1>

                <div style={styles.cardsContainer}>
                    <div onClick={() => handleStatusCardClick('pending')} style={styles.card}>
                        <div style={styles.cardHeader}>
                            <h3 style={styles.cardTitle}>Order Pending</h3>
                            <div style={styles.cardValueContainer}>
                                <FaShoppingCart style={styles.cardIcon} />
                            </div>
                        </div>
                        <p style={styles.cardValue}>{analytics?.orders?.pending || 0}</p>
                    </div>
                    <div onClick={() => handleStatusCardClick('confirmed')} style={styles.card}>
                        <div style={styles.cardHeader}>
                            <h3 style={styles.cardTitle}>Order Confirmed</h3>
                            <div style={styles.cardValueContainer}>
                                <FaShopSlash style={styles.cardIcon} />
                            </div>
                        </div>
                        <p style={styles.cardValue}>{analytics?.orders?.confirmed || 0}</p>
                    </div>
                    <div onClick={() => handleStatusCardClick('outForDelivery')} style={styles.card}>
                        <div style={styles.cardHeader}>
                            <h3 style={styles.cardTitle}>Order Out For Delivery</h3>
                            <div style={styles.cardValueContainer}>
                                <FaTruck style={styles.cardIcon} />
                            </div>
                        </div>
                        <p style={styles.cardValue}>{analytics?.orders?.outForDelivery || 0}</p>
                    </div>
                    <div onClick={() => handleStatusCardClick('delivered')} style={styles.card}>
                        <div style={styles.cardHeader}>
                            <h3 style={styles.cardTitle}>Order Deliverd</h3>
                            <div style={styles.cardValueContainer}>
                                <FaHome style={styles.cardIcon} />
                            </div>
                        </div>
                        <p style={styles.cardValue}>{analytics?.orders?.delivered || 0}</p>
                    </div>
                    <div onClick={() => handleStatusCardClick('returned')} style={styles.card}>
                        <div style={styles.cardHeader}>
                            <div>
                                <h3 style={styles.cardTitle}>Order Return</h3>
                                <p style={styles.cardValue}>{analytics?.orders?.returned || 0}</p>
                            </div>
                            <div style={styles.cardValueContainer}>
                                <FaDollarSign style={styles.cardIcon} />
                            </div>
                        </div>
                    </div>
                    <div onClick={() => handleStatusCardClick('cancelled')} style={styles.card}>
                        <div style={styles.cardHeader}>
                            <h3 style={styles.cardTitle}>Order Cancel</h3>
                            <div style={styles.cardValueContainer}>
                                <FaDiscord style={styles.cardIcon} />
                            </div>
                        </div>
                        <p style={styles.cardValue}>{analytics?.orders?.cancelled || 0}</p>
                    </div>
                </div>

                <div style={styles.productListContainer}>
                    <div style={styles.productListHeader}>
                        <div>
                            <h2 style={styles.allOrderListTitle}>
                                All Order list ({pagination.totalOrders})
                            </h2>
                        </div>
                        <div style={styles.searchFilterContainer}>
                            <div style={styles.searchBar}>
                                <FaSearch style={styles.searchIcon} />
                                <input
                                    type="text"
                                    style={styles.searchInput}
                                    placeholder="Search by order number or courier service..."
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                />
                            </div>
                            <button
                                style={styles.actionButton}
                                onClick={handlePrintOrders}
                            >
                                <FaPrint style={styles.actionButtonIcon} />
                                Print Orders
                            </button>
                        </div>
                    </div>

                    <div style={styles.tableWrapper}>
                        {(loading || filterLoading || isSearchLoading) ? (
                            <div style={styles.loadingContainer}>
                                <ClipLoader color="#E5D0A5" size={40} />
                            </div>
                        ) : orders.length === 0 ? (
                            <>
                                <div style={styles.bagIconContainer}>
                                    <img src={bag} alt="Shopping bag" style={styles.mainBagIcon} />
                                </div>
                                <div style={styles.emptyState}>
                                    {debouncedSearchTerm ? 'No orders found' : 'No Orders Yet'}
                                </div>
                            </>
                        ) : (
                            <table style={styles.table}>
                                <thead style={styles.tableHeader}>
                                    <tr>
                                        <th style={styles.th}>ORDER</th>
                                        <th style={styles.th}>QTY</th>
                                        <th style={styles.th}>AMOUNT</th>
                                        <th style={styles.th}>PAYMENT</th>
                                        <th style={styles.th}>REMARKS</th>
                                        <th style={styles.th}>STATUS</th>
                                        <th style={styles.th}>COURIER SERVICE</th>
                                        <th style={styles.th}>ACTIONS</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {orders.map((order) => (
                                        <tr key={order._id} style={styles.tableRow}>
                                            <td style={styles.td}>{order.orderNumber}</td>
                                            <td style={styles.td}>{order.qty}</td>
                                            <td style={styles.td}>${order.total.toFixed(2)}</td>
                                            <td style={styles.td}>
                                                <span
                                                    style={{
                                                        ...styles.statusPill,
                                                        ...(order.status === "delivered" ? styles.statusPaid : styles.statusPending),
                                                    }}
                                                >
                                                    {order.status === "delivered" ? "Paid" : "Pending"}
                                                </span>

                                            </td>
                                            <td style={styles.td}>{order.createdAt}</td>
                                            <td style={styles.td}>
                                                <span
                                                    style={{
                                                        ...styles.statusPill,
                                                        backgroundColor:
                                                            order.status === "pending" ? "#FBBF24" :
                                                                order.status === "confirmed" ? "#3B82F6" :
                                                                    order.status === "processing" ? "#6366F1" :
                                                                        order.status === "outForDelivery" ? "#06B6D4" :
                                                                            order.status === "delivered" ? "#10B981" :
                                                                                order.status === "orderDelivery" ? "#10B981" :
                                                                                    order.status === "cancelled" ? "#EF4444" :
                                                                                        order.status === "returned" ? "#A855F7" :
                                                                                            "#9CA3AF",
                                                        color: "#fff"
                                                    }}
                                                >
                                                    {formatStatus(order.status)}
                                                </span>
                                            </td>
                                            <td style={styles.td}>
                                                {order.courierService || "NA"}
                                            </td>
                                            <td style={styles.td}>
                                                <div style={styles.actionButtons}>
                                                    <button
                                                        style={styles.actionButton}
                                                        onClick={() => handleViewOrder(order._id)}
                                                    >
                                                        <FaEye style={styles.actionButtonIcon} />
                                                    </button>
                                                    <button
                                                        style={styles.actionButton}
                                                        onClick={() => handleEditOrder(order._id)}
                                                    >
                                                        <FaEdit style={styles.actionButtonIcon} />
                                                    </button>
                                                    <button
                                                        style={{ ...styles.actionButton, ...styles.actionButtonDanger }}
                                                        onClick={() => handleDeleteOrder(order._id)}
                                                    >
                                                        <FaTrash style={styles.actionButtonIcon} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    <div style={styles.paginationContainer}>
                        <div style={styles.paginationControls}>
                            <button
                                style={{
                                    ...styles.paginationButton,
                                    ...(pagination.page === 1 && { opacity: 0.5, cursor: 'not-allowed' })
                                }}
                                onClick={() => handlePageChange(pagination.page - 1)}
                                disabled={pagination.page === 1}
                            >
                                <FaAngleLeft />
                            </button>
                            {renderPaginationButtons()}
                            <button
                                style={{
                                    ...styles.paginationButton,
                                    ...(pagination.page === pagination.totalPages && { opacity: 0.5, cursor: 'not-allowed' })
                                }}
                                onClick={() => handlePageChange(pagination.page + 1)}
                                disabled={pagination.page === pagination.totalPages}
                            >
                                <FaAngleRight />
                            </button>
                        </div>
                        <p style={styles.paginationText}>
                            Showing {(pagination.page - 1) * pagination.limit + 1}-
                            {Math.min(pagination.page * pagination.limit, pagination.totalOrders)}
                            of {pagination.totalOrders} orders
                        </p>
                    </div>
                </div>

                {confirmDelete.open && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 1000
                    }}>
                        <div style={{
                            backgroundColor: '#000',
                            padding: '20px',
                            borderRadius: '8px',
                            color: '#E5D0A5',
                            maxWidth: '400px',
                            width: '90%'
                        }}>
                            <h3>Confirm Delete</h3>
                            <p>Are you sure you want to delete this order?</p>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                                <button
                                    style={{
                                        ...styles.actionButton,
                                        backgroundColor: '#333',
                                        color: '#E5D0A5'
                                    }}
                                    onClick={() => setConfirmDelete({ open: false, orderId: null })}
                                    disabled={deleting}
                                >
                                    Cancel
                                </button>
                                <button
                                    style={{
                                        ...styles.actionButton,
                                        ...styles.actionButtonDanger
                                    }}
                                    onClick={confirmDeletion}
                                    disabled={deleting}
                                >
                                    {deleting ? 'Deleting...' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}